import ky, { isHTTPError, isTimeoutError } from "ky";
import { atom } from "nanostores";
import isNetworkError from "is-network-error";
//#region src/api.ts
const DEFAULT_SERVICE_BASE_URLS = {
	dev: "https://forest-api.service.develop-cluster.seekrtech.com",
	staging: "https://forest-api-staging.seekrtech.com",
	global: "https://forest-api.seekrtech.com",
	china: "https://forest-api.upwardsware.com"
};
const DEFAULT_RECEIPT_BASE_URLS = {
	dev: "https://receipt.service.develop-cluster.seekrtech.com",
	staging: "https://receipt.staging.seekrtech.com",
	global: "https://receipt-system.seekrtech.com",
	china: "https://receipt-china.upwardsware.com"
};
const SERVICE_PLACEHOLDER = "https://__paywall_service__.invalid/";
const RECEIPT_PLACEHOLDER = "https://__paywall_receipt__.invalid/";
function createPaywallApi(options) {
	const { userInfo } = options;
	const serviceServerUrl = options.serviceBaseByServer ?? DEFAULT_SERVICE_BASE_URLS;
	const receiptSystemUrl = options.receiptBaseByServer ?? DEFAULT_RECEIPT_BASE_URLS;
	const base = ky.create({
		timeout: 2e4,
		hooks: {
			beforeRequest: [async (request) => {
				const value = await userInfo.getReady();
				if (value.user_type === "legacy") request.headers.set("Remember-Token", value.token);
				else request.headers.set("Authorization", `Bearer ${value.token}`);
				let realBase;
				let path;
				if (request.url.startsWith(SERVICE_PLACEHOLDER)) {
					path = request.url.slice(36);
					realBase = serviceServerUrl[value.server];
				} else if (request.url.startsWith(RECEIPT_PLACEHOLDER)) {
					path = request.url.slice(36);
					realBase = receiptSystemUrl[value.server];
				}
				if (realBase != null && path !== void 0) {
					const body = request.method === "GET" || request.method === "HEAD" ? void 0 : await request.clone().arrayBuffer();
					return new Request(`${realBase.replace(/\/$/, "")}/${path}`, {
						method: request.method,
						headers: request.headers,
						body,
						signal: request.signal,
						credentials: request.credentials,
						cache: request.cache,
						mode: request.mode,
						redirect: request.redirect,
						referrer: request.referrer,
						referrerPolicy: request.referrerPolicy,
						integrity: request.integrity,
						keepalive: request.keepalive
					});
				}
			}],
			afterResponse: [async (request, _, response) => {
				if (response.status === 401 || response.status === 403) {
					const value = userInfo.get();
					if (!value) throw new Error("User info not set");
					if (value.user_type === "legacy") return response;
					const refreshed = await userInfo.refresh();
					request.headers.set("Authorization", `Bearer ${refreshed.token}`);
					return await ky(request);
				}
				return response;
			}]
		}
	});
	return {
		service: base.extend({ prefixUrl: SERVICE_PLACEHOLDER }),
		receipt: base.extend({ prefixUrl: RECEIPT_PLACEHOLDER })
	};
}
//#endregion
//#region src/stores.ts
function createProductsStore(initial = null) {
	return atom(initial);
}
function createBooleanStore(initial) {
	return atom(initial);
}
//#endregion
//#region src/user-info-source.ts
function createUserInfoSource(options) {
	const store = atom(null);
	let loadPromise = null;
	function getReady() {
		const current = store.get();
		if (current) return Promise.resolve(current);
		if (loadPromise) return loadPromise;
		const inFlight = options.load().then((value) => {
			store.set(value);
			return value;
		}, (error) => {
			if (loadPromise === inFlight) loadPromise = null;
			throw error;
		});
		loadPromise = inFlight;
		return loadPromise;
	}
	async function refresh() {
		const value = await options.refresh();
		store.set(value);
		return value;
	}
	return {
		atom: store,
		get: () => store.get(),
		getReady,
		refresh
	};
}
//#endregion
//#region src/subscribe.ts
const PaymentSystemID = {
	PingXX: 7,
	GooglePlay: 8,
	AppStoreStoreKit1: 9,
	AppStoreStoreKit2: 11
};
const paymentSystemIdByPlatform = {
	china_android: PaymentSystemID.PingXX,
	googleplay_android: PaymentSystemID.GooglePlay,
	ios: PaymentSystemID.AppStoreStoreKit2
};
async function subscribe({ api, platform, userId, product, nativePurchase, closeTransaction, receiptSystemAccountId, offerId, offerCompactJws, shouldResolve, currentSubscriptionExpiresAt, debugSimulateAfterPaymentError, onStepError, logEvent }) {
	let purchaseId;
	try {
		purchaseId = (await api.service.post(`store/users/${userId}/purchase-right`, { json: {
			product_id: product.product_id,
			iap_sku: product.sku
		} }).json()).results[0].purchase.id;
	} catch (error) {
		const code = classifyErrorCode(error);
		let componentName = "";
		switch (code) {
			case "network":
			case "timeout":
				componentName = "internet_issue";
				break;
			default: componentName = `error_${code}`;
		}
		logEvent?.({
			event_name: "component_event",
			event_params: {
				type: "dialog",
				app_action: "show",
				component_name: componentName
			}
		});
		onStepError?.(error, { step: "create_order" });
		throw error;
	}
	const postTransaction = async (transactionId) => {
		let receiptRequestUrl = `projects/6/users/${userId}`;
		let data = {};
		if (platform === "china_android") {
			receiptRequestUrl += "/pending_iap_receipts";
			data = { receipt_data: transactionId };
		} else {
			receiptRequestUrl += "/subscriptions";
			data = { transaction_id: transactionId };
		}
		await api.receipt.post(receiptRequestUrl, { json: {
			...data,
			sku_id: product.sku,
			payment_system_id: paymentSystemIdByPlatform[platform]
		} });
	};
	const executeNativePurchase = async () => {
		const result = await nativePurchase(product.product_type === "subscription" ? {
			product_type: "subscription",
			product_id: product.product_id,
			sku: product.sku,
			offer_id: offerId,
			offer_compact_jws: offerCompactJws,
			receipt_system_account_id: receiptSystemAccountId
		} : {
			product_type: "consumable",
			product_id: product.product_id,
			sku: product.sku,
			receipt_system_account_id: receiptSystemAccountId
		});
		if (!result.success) {
			if (platform === "china_android" && product.product_type === "subscription" && result.error === "interrupted") {
				const recovery = await recoverInterruptedSubscription(api.service, userId, currentSubscriptionExpiresAt);
				if (recovery.status) return recovery.status;
				if (recovery.lastRequestError !== void 0) onStepError?.(recovery.lastRequestError, {
					step: "recover_interrupted_purchase",
					purchaseId
				});
			}
			const markAction = result.error === "interrupted" ? "mark-cancelled" : "mark-failed";
			api.service.post(`store/users/${userId}/purchase/${purchaseId}/${markAction}`).catch(() => {});
			const error = new NativeStoreError(result.error);
			logEvent?.({
				event_name: "component_event",
				event_params: {
					type: "dialog",
					app_action: "show",
					component_name: `sub_paid_fail_${error.message.replace(/ /g, "_")}`
				}
			});
			onStepError?.(error, {
				step: "native_purchase",
				purchaseId
			});
			throw error;
		}
		return result.data.transaction_id;
	};
	const initialPurchase = await executeNativePurchase();
	if (typeof initialPurchase !== "string") return initialPurchase;
	let transactionId = initialPurchase;
	if (debugSimulateAfterPaymentError) throw new AfterPaymentError(purchaseId, transactionId, "debug_simulation");
	try {
		await api.service.post(`store/users/${userId}/purchase/${purchaseId}/mark-processing`);
	} catch (error) {
		onStepError?.(error, {
			step: "mark_processing",
			purchaseId,
			transactionId
		});
		throw new AfterPaymentError(purchaseId, transactionId, "mark_processing");
	}
	try {
		await postTransaction(transactionId);
	} catch (error) {
		if (!shouldCloseTransactionAndRetry(error)) {
			onStepError?.(error, {
				step: "post_transaction",
				purchaseId,
				transactionId
			});
			throw new AfterPaymentError(purchaseId, transactionId, "post_transaction");
		}
		try {
			await closeTransaction({ transaction_id: transactionId });
		} catch (e) {
			console.error("Failed to close transaction before retrying", e);
		}
		const retryPurchase = await executeNativePurchase();
		if (typeof retryPurchase !== "string") return retryPurchase;
		transactionId = retryPurchase;
		try {
			await postTransaction(transactionId);
		} catch (retryError) {
			onStepError?.(retryError, {
				step: "post_transaction",
				purchaseId,
				transactionId
			});
			throw new AfterPaymentError(purchaseId, transactionId, "post_transaction");
		}
	}
	try {
		await closeTransaction({ transaction_id: transactionId });
	} catch (error) {
		onStepError?.(error, {
			step: "close_transaction",
			purchaseId,
			transactionId
		});
	}
	try {
		return await waitForSubscriptionStatus({
			serviceApi: api.service,
			userId,
			shouldResolve
		});
	} catch (error) {
		onStepError?.(error, {
			step: "poll_service_level",
			purchaseId,
			transactionId
		});
		console.error("Failed to poll service level status", error);
		return null;
	}
}
function classifyErrorCode(error) {
	if (isHTTPError(error)) return String(error.response.status);
	if (isTimeoutError(error)) return "timeout";
	if (isNetworkError(error)) return "network";
	return "unknown";
}
function shouldCloseTransactionAndRetry(error) {
	if (isHTTPError(error)) return error.response.status === 409 || error.response.status === 410 || error.response.status === 412;
	return false;
}
var NativeStoreError = class extends Error {
	constructor(message) {
		super(message);
		this.name = "NativeStoreError";
	}
};
var AfterPaymentError = class extends Error {
	purchaseId;
	transactionId;
	node;
	constructor(purchaseId, transactionId, node) {
		super("Purchase failed");
		this.name = "AfterPaymentError";
		this.purchaseId = purchaseId;
		this.transactionId = transactionId;
		this.node = node;
	}
};
const SERVICE_LEVEL_POLL_INTERVAL_MS = 100;
const SERVICE_LEVEL_POLL_TIMEOUT_MS = 15e3;
const INTERRUPTED_RECOVERY_DELAYS_MS = [
	0,
	1e3,
	2e3
];
const INTERRUPTED_RECOVERY_REQUEST_TIMEOUT_MS = 1e4;
async function waitForSubscriptionStatus({ serviceApi, userId, intervalMs = SERVICE_LEVEL_POLL_INTERVAL_MS, timeoutMs = SERVICE_LEVEL_POLL_TIMEOUT_MS, signal, shouldResolve = isSubscriptionStatus }) {
	const controller = new AbortController();
	const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
	const combinedSignal = mergeAbortSignals(signal, controller.signal);
	try {
		return await pollServiceLevelStatus(serviceApi, userId, combinedSignal, intervalMs, shouldResolve);
	} catch (error) {
		if (combinedSignal?.aborted) throw new Error("Polling service level timed out");
		throw error;
	} finally {
		clearTimeout(timeoutId);
	}
}
async function pollServiceLevelStatus(serviceApi, userId, signal, intervalMs, shouldResolve) {
	while (true) {
		if (signal?.aborted) throw new Error("Polling service level aborted");
		const status = await serviceApi.get(`user/users/${userId}/service-levels/status`, { signal }).json();
		if (shouldResolve(status)) return status;
		await sleep(intervalMs, signal);
	}
}
function isSubscriptionStatus(status) {
	return status.service_level === "subscription";
}
async function recoverInterruptedSubscription(serviceApi, userId, currentSubscriptionExpiresAt) {
	let lastRequestError;
	for (const delayMs of INTERRUPTED_RECOVERY_DELAYS_MS) {
		if (delayMs > 0) await sleep(delayMs);
		try {
			const status = await serviceApi.get(`user/users/${userId}/service-levels/status`, {
				retry: 0,
				timeout: INTERRUPTED_RECOVERY_REQUEST_TIMEOUT_MS
			}).json();
			if (isRecoveredSubscriptionStatus(status, currentSubscriptionExpiresAt)) return { status };
		} catch (error) {
			lastRequestError = error;
		}
	}
	return {
		status: null,
		lastRequestError
	};
}
function isRecoveredSubscriptionStatus(status, currentSubscriptionExpiresAt) {
	if (!isSubscriptionStatus(status)) return false;
	if (currentSubscriptionExpiresAt == null) return true;
	const recoveredExpiry = Date.parse(status.subscription.expires_at);
	const currentExpiry = Date.parse(currentSubscriptionExpiresAt);
	return Number.isFinite(recoveredExpiry) && Number.isFinite(currentExpiry) && recoveredExpiry > currentExpiry;
}
function sleep(ms, signal) {
	return new Promise((resolve, reject) => {
		const timer = setTimeout(resolve, ms);
		if (signal) signal.addEventListener("abort", () => {
			clearTimeout(timer);
			reject(/* @__PURE__ */ new Error("Polling service level aborted"));
		}, { once: true });
	});
}
function mergeAbortSignals(...signals) {
	const activeSignals = signals.filter(Boolean);
	if (activeSignals.length === 0) return void 0;
	const controller = new AbortController();
	const onAbort = () => controller.abort();
	activeSignals.forEach((sig) => sig.addEventListener("abort", onAbort));
	if (activeSignals.some((sig) => sig.aborted)) controller.abort();
	return controller.signal;
}
//#endregion
//#region src/types.ts
function mapPlatformToServiceApi(platform) {
	switch (platform) {
		case "ios": return "ios";
		case "googleplay_android": return "android";
		case "china_android": return "android-cn";
	}
}
//#endregion
//#region src/packs.ts
const PRESETS = {
	staging: {
		ios: {
			annual_default: {
				sku: "forest_annual_1",
				receipt_system_product_id: 2,
				product_id: 21
			},
			annual_early_bird: {
				sku: "forest_annual_early_bird",
				receipt_system_product_id: 6,
				product_id: 22
			},
			monthly_default: {
				sku: "forest_monthly_1",
				receipt_system_product_id: 1,
				product_id: 37
			},
			monthly_early_bird: {
				sku: "forest_monthly_early_bird",
				receipt_system_product_id: 5,
				product_id: 38
			}
		},
		googleplay_android: {
			annual_default: {
				sku: "forest_plus-annual-1",
				receipt_system_product_id: 4,
				product_id: 23
			},
			annual_early_bird: {
				sku: "forest_plus-annual-early-bird",
				receipt_system_product_id: 8,
				product_id: 24
			},
			monthly_default: {
				sku: "forest_plus-monthly-1",
				receipt_system_product_id: 3,
				product_id: 39
			},
			monthly_early_bird: {
				sku: "forest_plus-monthly-early-bird",
				receipt_system_product_id: 7,
				product_id: 40
			}
		},
		china_android: {
			annual_default: {
				sku: "plus_card_annual_1",
				receipt_system_product_id: 23,
				product_id: 46
			},
			annual_early_bird: {
				sku: "plus_card_annual_early_bird_1",
				receipt_system_product_id: 24,
				product_id: 47
			},
			monthly_default: {
				sku: "plus_card_monthly_1",
				receipt_system_product_id: 21,
				product_id: 44
			},
			monthly_early_bird: {
				sku: "plus_card_monthly_early_bird_1",
				receipt_system_product_id: 22,
				product_id: 45
			}
		}
	},
	global: {
		ios: {
			annual_default: {
				sku: "forest_annual_1",
				receipt_system_product_id: 2,
				product_id: 15
			},
			annual_early_bird: {
				sku: "forest_annual_early_bird",
				receipt_system_product_id: 6,
				product_id: 16
			},
			monthly_default: {
				sku: "forest_monthly_1",
				receipt_system_product_id: 1,
				product_id: 27
			},
			monthly_early_bird: {
				sku: "forest_monthly_early_bird",
				receipt_system_product_id: 5,
				product_id: 28
			}
		},
		googleplay_android: {
			annual_default: {
				sku: "forest_plus-annual-1",
				receipt_system_product_id: 4,
				product_id: 17
			},
			annual_early_bird: {
				sku: "forest_plus-annual-early-bird",
				receipt_system_product_id: 8,
				product_id: 18
			},
			monthly_default: {
				sku: "forest_plus-monthly-1",
				receipt_system_product_id: 3,
				product_id: 29
			},
			monthly_early_bird: {
				sku: "forest_plus-monthly-early-bird",
				receipt_system_product_id: 7,
				product_id: 30
			}
		},
		china_android: {
			annual_default: {
				sku: "plus_card_annual_1",
				receipt_system_product_id: 23,
				product_id: 36
			},
			annual_early_bird: {
				sku: "plus_card_annual_early_bird_1",
				receipt_system_product_id: 24,
				product_id: 37
			},
			monthly_default: {
				sku: "plus_card_monthly_1",
				receipt_system_product_id: 21,
				product_id: 34
			},
			monthly_early_bird: {
				sku: "plus_card_monthly_early_bird_1",
				receipt_system_product_id: 22,
				product_id: 35
			}
		}
	},
	china: {
		ios: {
			annual_default: {
				sku: "forest_annual_1",
				receipt_system_product_id: 2,
				product_id: 15
			},
			annual_early_bird: {
				sku: "forest_annual_early_bird",
				receipt_system_product_id: 6,
				product_id: 16
			},
			monthly_default: {
				sku: "forest_monthly_1",
				receipt_system_product_id: 1,
				product_id: 27
			},
			monthly_early_bird: {
				sku: "forest_monthly_early_bird",
				receipt_system_product_id: 5,
				product_id: 28
			}
		},
		googleplay_android: {
			annual_default: {
				sku: "forest_plus-annual-1",
				receipt_system_product_id: 4,
				product_id: 17
			},
			annual_early_bird: {
				sku: "forest_plus-annual-early-bird",
				receipt_system_product_id: 8,
				product_id: 18
			},
			monthly_default: {
				sku: "forest_plus-monthly-1",
				receipt_system_product_id: 3,
				product_id: 29
			},
			monthly_early_bird: {
				sku: "forest_plus-monthly-early-bird",
				receipt_system_product_id: 7,
				product_id: 30
			}
		},
		china_android: {
			annual_default: {
				sku: "plus_card_annual_1",
				receipt_system_product_id: 23,
				product_id: 36
			},
			annual_early_bird: {
				sku: "plus_card_annual_early_bird_1",
				receipt_system_product_id: 24,
				product_id: 37
			},
			monthly_default: {
				sku: "plus_card_monthly_1",
				receipt_system_product_id: 21,
				product_id: 34
			},
			monthly_early_bird: {
				sku: "plus_card_monthly_early_bird_1",
				receipt_system_product_id: 22,
				product_id: 35
			}
		}
	}
};
function resolvePreset(server) {
	if (server === "dev") return PRESETS.staging;
	return PRESETS[server];
}
function createSubscriptionPacks(options) {
	const { server, platform } = options;
	const platformValues = resolvePreset(server)[platform];
	if (!platformValues) throw new Error(`Missing pack config for platform: ${platform}`);
	return {
		annualEarlyBird: toMetadata(platformValues.annual_early_bird),
		annualDefault: toMetadata(platformValues.annual_default),
		monthlyEarlyBird: toMetadata(platformValues.monthly_early_bird),
		monthlyDefault: toMetadata(platformValues.monthly_default)
	};
}
function toMetadata(values) {
	return {
		product_type: "subscription",
		sku: values.sku,
		receipt_system_product_id: values.receipt_system_product_id,
		product_id: values.product_id
	};
}
//#endregion
//#region src/auth.ts
var AbortError = class extends Error {
	constructor(message = "aborted") {
		super(message);
		this.name = "AbortError";
	}
};
async function ensureAuthenticatedUser(options) {
	let userInfo = await options.getUserInfo().then((result) => {
		if (!result.success) throw new Error("getUserInfo failed");
		if (!result.data) throw new Error("getUserInfo returned null data");
		return result.data;
	});
	const wasGuest = userInfo.user_type === "guest";
	const guestUserId = userInfo.user_id;
	if (userInfo.user_type === "guest") {
		await options.onBeforeGuestUpgrade?.();
		const guestAuthResult = await options.auth();
		if (!guestAuthResult.success) throw new AbortError();
		if (!guestAuthResult.data) throw new Error("auth returned null data");
		userInfo = guestAuthResult.data;
	}
	if (userInfo.user_type === "legacy") {
		await options.onBeforeLegacyBind?.();
		if (!(await options.bindLegacyUser()).success) throw new AbortError();
		await options.onLegacyBindStart?.();
		while (true) {
			const legacyAuthResult = await options.auth();
			if (!legacyAuthResult.success) continue;
			if (!legacyAuthResult.data) continue;
			userInfo = legacyAuthResult.data;
			break;
		}
		await options.onLegacyBindEnd?.();
	}
	if (userInfo.user_type !== "seekr_id") throw new Error("Somehow not authenticated");
	if (wasGuest && userInfo.user_id !== guestUserId) await options.onAfterGuestUpgrade?.();
	await options.onUserInfo?.(userInfo);
	return userInfo;
}
//#endregion
//#region src/trial.ts
const TRIAL_PRODUCT_IDS = {
	dev: 43,
	staging: 43,
	production: 33
};
/**
* Get China Android free trial product id by build mode.
* Returns undefined when not configured for the provided mode.
*/
function getChinaAndroidTrialProductId(mode) {
	return TRIAL_PRODUCT_IDS[mode];
}
//#endregion
//#region src/price.ts
/**
* Universal price parsing and formatting utilities for paywall apps.
*
* Handles localized price strings from native IAP systems with various formats:
* - Currency prefix: "$60.00", "€45.00", "£30.00"
* - Currency suffix: "60.00 S₣", "45,00 €"
* - Different decimal separators: "60.00" vs "60,00"
*/
/**
* Removes trailing zeros from a price string while preserving currency symbols.
*
* @example
* removeTrailingZeros("$10.50") // "$10.5"
* removeTrailingZeros("$10.00") // "$10"
* removeTrailingZeros("50.00 S₣") // "50 S₣"
* removeTrailingZeros("€45,00") // "€45"
*/
function removeTrailingZeros(price) {
	const notThousands = "(?!\\d{3}(?:\\s|[^\\d]|$))";
	return price.replace(new RegExp(`([.,])${notThousands}0+(?=\\s|[^\\d]|$)`, "g"), "").replace(new RegExp(`([.,])${notThousands}(\\d*?)0+(?=\\s|[^\\d]|$)`, "g"), "$1$2").replace(new RegExp(`([.,])${notThousands}(?=\\s|[^\\d]|$)`, "g"), "");
}
/**
* Parses a localized price string into its components.
*
* @example
* parsePriceComponents("$60.00") // { value: 60, currency: "$", isCurrencyPrefix: true, decimalSeparator: "." }
* parsePriceComponents("45,00 €") // { value: 45, currency: "€", isCurrencyPrefix: false, decimalSeparator: "," }
*/
function parsePriceComponents(priceLocalized) {
	const numericStr = priceLocalized.match(/[\d]+(?:[.,]\d+)*/g)?.[0] ?? "0";
	const lastDotIndex = numericStr.lastIndexOf(".");
	const lastCommaIndex = numericStr.lastIndexOf(",");
	let decimalSeparator;
	let value;
	if (lastDotIndex > -1 && lastCommaIndex > -1) if (lastDotIndex > lastCommaIndex) {
		decimalSeparator = ".";
		value = parseFloat(numericStr.replace(/,/g, ""));
	} else {
		decimalSeparator = ",";
		value = parseFloat(numericStr.replace(/\./g, "").replace(",", "."));
	}
	else if (lastCommaIndex > -1) if (numericStr.length - lastCommaIndex - 1 === 3) {
		decimalSeparator = ".";
		value = parseFloat(numericStr.replace(/,/g, ""));
	} else {
		decimalSeparator = ",";
		const intPart = numericStr.substring(0, lastCommaIndex).replace(/,/g, "");
		const decPart = numericStr.substring(lastCommaIndex + 1);
		value = parseFloat(`${intPart}.${decPart}`);
	}
	else if (lastDotIndex > -1) if (numericStr.length - lastDotIndex - 1 === 3) {
		decimalSeparator = ",";
		value = parseFloat(numericStr.replace(/\./g, ""));
	} else {
		decimalSeparator = ".";
		value = parseFloat(numericStr);
	}
	else {
		decimalSeparator = ".";
		value = parseFloat(numericStr);
	}
	const currency = priceLocalized.replace(/[\d.,\s]/g, "").trim();
	const isCurrencyPrefix = !priceLocalized.trim().match(/^[\d]/);
	return {
		value,
		currency,
		isCurrencyPrefix,
		decimalSeparator
	};
}
/**
* Formats a numeric value using the format from a localized price string.
* Preserves currency symbol position and decimal separator.
*
* @example
* formatPriceWithLocale(5, "$60.00") // "$5"
* formatPriceWithLocale(5.5, "$60.00") // "$5.5"
* formatPriceWithLocale(5, "60,00 €") // "5 €"
*/
function formatPriceWithLocale(value, priceLocalized, options = {}) {
	const { decimals = 2, removeTrailingZeros: shouldRemoveZeros = true } = options;
	const { currency, isCurrencyPrefix, decimalSeparator } = parsePriceComponents(priceLocalized);
	let formattedValue = value.toFixed(decimals);
	if (decimalSeparator === ",") formattedValue = formattedValue.replace(".", ",");
	let result;
	if (isCurrencyPrefix) result = `${currency}${formattedValue}`;
	else result = `${formattedValue} ${currency}`;
	if (shouldRemoveZeros) result = removeTrailingZeros(result);
	return result;
}
/**
* Calculates the monthly price from an annual subscription price.
* Preserves the original price format (currency position, decimal separator).
*
* @example
* calculateMonthlyPrice({ price_localized: "$60.00", price_value: 60 })
* // "$5"
*
* calculateMonthlyPrice({ price_localized: "120,00 €", price_value: 120 })
* // "10 €"
*/
function calculateMonthlyPrice(product) {
	return formatPriceWithLocale(product.price_value / 12, product.price_localized);
}
/**
* Replaces the numeric value in a localized price string with a new value.
* Useful for displaying calculated prices (e.g., per-month) while preserving
* the original currency format.
*
* @example
* replacePrice("$60.00", 5) // "$5"
* replacePrice("60,00 €", 5) // "5 €"
* replacePrice("S₣ 120.00", 10) // "S₣ 10"
*/
function replacePrice(priceLocalized, newValue, options = {}) {
	return formatPriceWithLocale(newValue, priceLocalized, options);
}
//#endregion
//#region src/experiment-products.ts
/**
* Fetches products for the pricing experiment from the Service API.
*
* @param serviceApi - The ky instance for the service API
* @param options - Options including userId, platform, and optional storeRegion
* @returns Array of experiment products
*/
async function fetchExperimentProducts(serviceApi, options) {
	const { userId, platform, storeRegion } = options;
	const searchParams = {
		platform,
		product_type: "subscription"
	};
	if (storeRegion) searchParams.store_region = storeRegion;
	return await serviceApi.get(`store/users/${userId}/products`, { searchParams }).json();
}
/**
* Infers product type (annual/monthly, early bird/default) from the product title.
*
* @param title - The product title from the API
* @returns Object with isAnnual and isEarlyBird flags
*/
function inferProductType(title) {
	const lowerTitle = title.toLowerCase();
	return {
		isAnnual: lowerTitle.includes("年方案") || lowerTitle.includes("annual") || lowerTitle.includes("yearly") || lowerTitle.includes("year"),
		isEarlyBird: lowerTitle.includes("早鳥") || lowerTitle.includes("early bird") || lowerTitle.includes("earlybird") || lowerTitle.includes("early_bird")
	};
}
/**
* Gets the platform-specific SKU from an experiment product.
*
* @param product - The experiment product
* @param platform - The payment platform
* @returns The appropriate SKU for the given platform
*/
function getSkuForPlatform(product, platform) {
	switch (platform) {
		case "ios": return product.iap_sku_ios;
		case "googleplay_android": return product.iap_sku_android;
		case "china_android": return product.iap_sku_android_cn;
	}
}
//#endregion
//#region src/restore.ts
var RestoreNoTransactionError = class extends Error {
	constructor() {
		super("No transactions found");
		this.name = "RestoreNoTransactionError";
	}
};
var RestoreError = class extends Error {
	status;
	constructor(message, status) {
		super(message);
		this.name = "RestoreError";
		this.status = status;
	}
};
async function restoreSubscription(options) {
	const { api, userId, platform, getAllUnclosedTransactions, getAllEntitlements, closeTransaction, currentSubscriptionExpiresAt } = options;
	const [transactions, entitlements] = await Promise.all([getAllUnclosedTransactions().then((result) => {
		if (!result.success) return [];
		return result.data.transactions.filter((transaction) => transaction.product_type === "subscription");
	}), getAllEntitlements().then((result) => {
		if (!result.success) return [];
		return result.data.transactions.filter((transaction) => transaction.product_type === "subscription");
	})]);
	let transaction;
	if (transactions.length > 0) {
		const [latestTransaction] = transactions.sort((a, b) => new Date(b.purchased_at).getTime() - new Date(a.purchased_at).getTime());
		transaction = latestTransaction;
	} else if (entitlements.length > 0) {
		const [latestEntitlement] = entitlements.sort((a, b) => new Date(b.purchased_at).getTime() - new Date(a.purchased_at).getTime());
		transaction = latestEntitlement;
	} else throw new RestoreNoTransactionError();
	const { data: openedPurchases } = await api.service.get(`store/users/${userId}/purchase`, { searchParams: {
		status: "open",
		page_size: 10,
		page: 1
	} }).json();
	const [openedPurchase] = openedPurchases.filter((purchase) => purchase.purchase_type === (platform === "china_android" ? "iap" : "subscription") && purchase.iap_sku === transaction.sku).sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
	if (openedPurchase) await api.service.post(`store/users/${userId}/purchase/${openedPurchase.id}/mark-processing`);
	let receiptRequestUrl = `projects/6/users/${userId}`;
	let data = {};
	if (platform === "china_android") {
		receiptRequestUrl += "/pending_iap_receipts";
		data = { receipt_data: transaction.transaction_id };
	} else {
		receiptRequestUrl += "/subscriptions";
		data = { transaction_id: transaction.transaction_id };
	}
	await api.receipt.post(receiptRequestUrl, { json: {
		...data,
		sku_id: transaction.sku,
		payment_system_id: paymentSystemIdByPlatform[platform]
	} });
	await closeTransaction(transaction);
	try {
		return await waitForSubscriptionStatus({
			serviceApi: api.service,
			userId,
			shouldResolve: (status) => {
				return status.service_level === "subscription" && status.subscription.expires_at !== currentSubscriptionExpiresAt;
			}
		});
	} catch {
		return null;
	}
}
//#endregion
//#region src/subscribe-flow.ts
function classifyPurchaseError(error) {
	if (error instanceof NativeStoreError) return {
		kind: "payment_timeout",
		detail: error.message
	};
	if (error instanceof AfterPaymentError) return { kind: "illegal_transaction_restore" };
	if (isHTTPError(error)) return {
		kind: "http_error",
		status: error.response.status
	};
	if (isTimeoutError(error) || isNetworkError(error)) return { kind: "no_internet" };
	return {
		kind: "unknown",
		code: error instanceof Error ? error.name + error.message : String(error)
	};
}
function deriveSuccessFacts(status, product) {
	const isSubscription = status?.service_level === "subscription";
	return {
		isSubscription,
		isTrialSuccess: isSubscription && status.subscription.is_in_trial,
		expiresAt: isSubscription ? status.subscription.expires_at : void 0,
		sku: product.sku
	};
}
function createSubscribeFlow(config) {
	const { api, platform, native, telemetry, getUserId, getReceiptSystemAccountId, getDebugSimulate, getCurrentSubscriptionExpiresAt } = config;
	const run = async (product) => {
		const userId = await getUserId();
		const receiptSystemAccountId = getReceiptSystemAccountId();
		native.logEvent({
			event_name: "receipt_system_account_id_status",
			event_params: { is_present: String(!!receiptSystemAccountId) }
		});
		const currentSubscriptionExpiresAt = getCurrentSubscriptionExpiresAt?.();
		const shouldResolve = getCurrentSubscriptionExpiresAt ? buildShouldResolve(currentSubscriptionExpiresAt) : void 0;
		const onStepError = telemetry ? buildStepTelemetry(telemetry, platform, product.sku) : void 0;
		return subscribe({
			api,
			platform,
			userId,
			product,
			nativePurchase: native.purchase,
			closeTransaction: native.closeTransaction,
			receiptSystemAccountId,
			shouldResolve,
			currentSubscriptionExpiresAt,
			debugSimulateAfterPaymentError: getDebugSimulate?.(),
			logEvent: native.logEvent,
			onStepError
		});
	};
	const handleSuccess = (status, product) => {
		native.logEvent({
			event_name: "product_sku_purchased_success",
			event_params: { sku_id: product.sku }
		});
		const facts = deriveSuccessFacts(status, product);
		native.logEvent({
			event_name: facts.isTrialSuccess ? "plus_trial_success" : "plus_subscribe_success",
			event_params: { sku_id: product.sku }
		});
		native.refreshServiceLevel();
		return facts;
	};
	const restore = async () => {
		const userId = await getUserId();
		return restoreSubscription({
			api,
			userId,
			platform,
			getAllUnclosedTransactions: native.getAllUnclosedTransactions,
			getAllEntitlements: native.getAllEntitlements,
			closeTransaction: native.closeTransaction,
			currentSubscriptionExpiresAt: getCurrentSubscriptionExpiresAt?.()
		});
	};
	return {
		run,
		handleSuccess,
		restore
	};
}
function buildShouldResolve(currentSubscriptionExpiresAt) {
	return (status) => status.service_level === "subscription" && status.subscription.expires_at !== currentSubscriptionExpiresAt;
}
function buildStepTelemetry(telemetry, platform, productSku) {
	return (error, context) => {
		if (context.step === "native_purchase" && error instanceof NativeStoreError && error.message === "interrupted") return;
		if (error instanceof Error) error.name = "SubscriptionError";
		telemetry.captureException(error, {
			step: context.step,
			purchase_id: context.purchaseId,
			transaction_id: context.transactionId,
			platform,
			product_sku: productSku
		});
	};
}
//#endregion
export { AbortError, AfterPaymentError, DEFAULT_RECEIPT_BASE_URLS, DEFAULT_SERVICE_BASE_URLS, NativeStoreError, PaymentSystemID, RestoreError, RestoreNoTransactionError, calculateMonthlyPrice, classifyPurchaseError, createBooleanStore, createPaywallApi, createProductsStore, createSubscribeFlow, createSubscriptionPacks, createUserInfoSource, deriveSuccessFacts, ensureAuthenticatedUser, fetchExperimentProducts, formatPriceWithLocale, getChinaAndroidTrialProductId, getSkuForPlatform, inferProductType, mapPlatformToServiceApi, parsePriceComponents, paymentSystemIdByPlatform, removeTrailingZeros, replacePrice, restoreSubscription, subscribe, waitForSubscriptionStatus };

//# sourceMappingURL=index.js.map